import { Alert, LoadingPanel } from '@dispatch/ui';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import type { ActiveSupportAccess, PlatformBusiness, SupportAccessRecord } from './types.js';

interface Props {
  csrfToken: string;
  activeSupportAccess?: ActiveSupportAccess | undefined;
  onSupportAccessChanged: (value: ActiveSupportAccess | undefined) => void;
}

export function PlatformPanel({ csrfToken, activeSupportAccess, onSupportAccessChanged }: Props) {
  const [businesses, setBusinesses] = useState<PlatformBusiness[]>([]);
  const [sessions, setSessions] = useState<SupportAccessRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();

  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [slug, setSlug] = useState('');
  const [superadminName, setSuperadminName] = useState('');
  const [superadminEmail, setSuperadminEmail] = useState('');

  const [supportBusinessId, setSupportBusinessId] = useState('');
  const [supportReason, setSupportReason] = useState('');
  const [supportDuration, setSupportDuration] = useState(3600);

  async function load(signal?: AbortSignal) {
    try {
      const [businessResult, accessResult] = await Promise.all([
        apiRequest<{ businesses: PlatformBusiness[] }>('/api/v1/platform/businesses', {
          ...(signal ? { signal } : {}),
        }),
        apiRequest<{ sessions: SupportAccessRecord[] }>('/api/v1/platform/support-access', {
          ...(signal ? { signal } : {}),
        }),
      ]);
      setBusinesses(businessResult.businesses);
      setSessions(accessResult.sessions);
      if (!supportBusinessId && businessResult.businesses[0]) {
        setSupportBusinessId(businessResult.businesses[0].id);
      }
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    void load(controller.signal);
    return () => controller.abort();
  }, []);

  async function createBusiness(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy('create');
    setError(undefined);
    setMessage(undefined);
    try {
      const created = await apiRequest<{
        id: string;
        legalName: string;
        slug: string;
        superadminMembershipId: string;
        setupRequired: boolean;
      }>('/api/v1/platform/businesses', {
        method: 'POST',
        csrfToken,
        body: {
          legalName,
          tradingName,
          slug,
          timezone: 'Europe/London',
          countryCode: 'GB',
          currencyCode: 'GBP',
          superadmin: { email: superadminEmail, displayName: superadminName },
        },
      });
      setMessage(
        created.setupRequired
          ? `${created.legalName} was created and an account setup email was queued for ${superadminEmail}.`
          : `${created.legalName} was created with one active Business Superadmin membership.`,
      );
      setLegalName('');
      setTradingName('');
      setSlug('');
      setSuperadminName('');
      setSuperadminEmail('');
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(undefined);
    }
  }

  async function startSupportAccess(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy('support');
    setError(undefined);
    setMessage(undefined);
    try {
      const access = await apiRequest<ActiveSupportAccess>('/api/v1/platform/support-access', {
        method: 'POST',
        csrfToken,
        body: {
          businessId: supportBusinessId,
          reason: supportReason,
          durationSeconds: supportDuration,
        },
      });
      sessionStorage.setItem('dispatch-support-access', JSON.stringify(access));
      onSupportAccessChanged(access);
      setSupportReason('');
      setMessage(`Support Access started for ${access.businessName}.`);
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(undefined);
    }
  }

  async function endSupportAccess() {
    if (!activeSupportAccess) return;
    setBusy('end-support');
    setError(undefined);
    setMessage(undefined);
    try {
      await apiRequest(`/api/v1/platform/support-access/${activeSupportAccess.id}/end`, {
        method: 'POST',
        csrfToken,
      });
      sessionStorage.removeItem('dispatch-support-access');
      onSupportAccessChanged(undefined);
      setMessage('Support Access ended.');
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(undefined);
    }
  }

  if (loading) return <LoadingPanel message="Loading platform administration…" />;

  return (
    <div>
      {error ? <Alert title="Platform action failed">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      {activeSupportAccess ? (
        <section className="support-banner" aria-labelledby="active-support-heading">
          <h2 id="active-support-heading">Support Access active</h2>
          <p>
            <strong>{activeSupportAccess.businessName}</strong> until{' '}
            {new Date(activeSupportAccess.expiresAt).toLocaleString()}.
          </p>
          <p>{activeSupportAccess.reason}</p>
          <button
            type="button"
            className="danger"
            disabled={busy === 'end-support'}
            onClick={() => void endSupportAccess()}
          >
            {busy === 'end-support' ? 'Ending…' : 'End Support Access'}
          </button>
        </section>
      ) : null}

      <section className="panel" aria-labelledby="create-business-heading">
        <h2 id="create-business-heading">Onboard business</h2>
        <form className="form-grid" onSubmit={(event) => void createBusiness(event)}>
          <div className="form-grid form-grid--two">
            <div className="form-field">
              <label htmlFor="legal-name">Legal name</label>
              <input
                id="legal-name"
                required
                maxLength={200}
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="trading-name">Trading name</label>
              <input
                id="trading-name"
                required
                maxLength={200}
                value={tradingName}
                onChange={(event) => setTradingName(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="business-slug">Tenant name</label>
              <input
                id="business-slug"
                required
                minLength={2}
                maxLength={80}
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                value={slug}
                onChange={(event) => setSlug(event.target.value.toLowerCase())}
              />
            </div>
          </div>
          <fieldset>
            <legend>First Business Superadmin</legend>
            <div className="form-grid form-grid--two">
              <div className="form-field">
                <label htmlFor="superadmin-name">Display name</label>
                <input
                  id="superadmin-name"
                  required
                  maxLength={160}
                  value={superadminName}
                  onChange={(event) => setSuperadminName(event.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="superadmin-email">Email address</label>
                <input
                  id="superadmin-email"
                  type="email"
                  required
                  value={superadminEmail}
                  onChange={(event) => setSuperadminEmail(event.target.value)}
                />
              </div>
            </div>
          </fieldset>
          <div className="actions">
            <button type="submit" disabled={busy === 'create'}>
              {busy === 'create' ? 'Creating…' : 'Create business'}
            </button>
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="businesses-heading">
        <h2 id="businesses-heading">Businesses</h2>
        {businesses.length === 0 ? <p>No businesses have been created.</p> : null}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Business</th>
                <th>Tenant</th>
                <th>Business Superadmin</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((business) => (
                <tr key={business.id}>
                  <td>{business.legal_name}</td>
                  <td>{business.slug}</td>
                  <td>
                    {business.business_superadmin_name ?? 'Not returned'}
                    {business.business_superadmin_email ? (
                      <>
                        <br />
                        <span className="help-text">{business.business_superadmin_email}</span>
                      </>
                    ) : null}
                  </td>
                  <td>{business.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel" aria-labelledby="support-access-heading">
        <h2 id="support-access-heading">Start Support Access</h2>
        <p className="lead">
          A reason is mandatory. Every tenant read and write performed during the session is
          audited.
        </p>
        {activeSupportAccess ? (
          <Alert tone="warning">
            End the current Support Access session before starting another.
          </Alert>
        ) : null}
        <form className="form-grid" onSubmit={(event) => void startSupportAccess(event)}>
          <div className="form-field">
            <label htmlFor="support-business">Business</label>
            <select
              id="support-business"
              required
              disabled={Boolean(activeSupportAccess)}
              value={supportBusinessId}
              onChange={(event) => setSupportBusinessId(event.target.value)}
            >
              <option value="">Select a business</option>
              {businesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.legal_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="support-reason">Reason</label>
            <textarea
              id="support-reason"
              required
              minLength={10}
              maxLength={500}
              disabled={Boolean(activeSupportAccess)}
              value={supportReason}
              onChange={(event) => setSupportReason(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="support-duration">Duration</label>
            <select
              id="support-duration"
              disabled={Boolean(activeSupportAccess)}
              value={supportDuration}
              onChange={(event) => setSupportDuration(Number(event.target.value))}
            >
              <option value={900}>15 minutes</option>
              <option value={1800}>30 minutes</option>
              <option value={3600}>1 hour</option>
              <option value={7200}>2 hours</option>
              <option value={14400}>4 hours</option>
            </select>
          </div>
          <div className="actions">
            <button
              type="submit"
              disabled={Boolean(activeSupportAccess) || busy === 'support' || !supportBusinessId}
            >
              {busy === 'support' ? 'Starting…' : 'Start Support Access'}
            </button>
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="support-history-heading">
        <h2 id="support-history-heading">Support Access history</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Business</th>
                <th>Reason</th>
                <th>Started</th>
                <th>Expires</th>
                <th>Ended</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <td>{session.business_name}</td>
                  <td>{session.reason}</td>
                  <td>{new Date(session.started_at).toLocaleString()}</td>
                  <td>{new Date(session.expires_at).toLocaleString()}</td>
                  <td>
                    {session.ended_at ? new Date(session.ended_at).toLocaleString() : 'Active'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
